import React from 'react';
import { Callout, DirectionalHint, Stack, Text } from '@fluentui/react';
import { useTimeline } from './TimelineContext';
import { buildSubtreeForFlamegraph } from './timelineUtils';
import { FlameGraphCustom } from '../FlameGraphCustom.tsx';

export function StackPopup({ timelineData }) {
    const {
        hoveredFrame,
        clickedFrame,
        popupPosition,
        handleDismiss
    } = useTimeline();

    // Create target element at mouse position - MUST be at top level
    const targetRef = React.useRef(null);

    // Determine which frame to show (click takes precedence)
    const activeFrame = clickedFrame || hoveredFrame;
    const isClickMode = !!clickedFrame;

    // Build ProfileNode tree for flamegraph
    const flamegraphData = React.useMemo(() => {
        if (!activeFrame) return null;

        // Both hover and click: show target node + descendants only (no ancestors)
        return buildSubtreeForFlamegraph(
            activeFrame.stack,
            activeFrame.group,
            timelineData,
            false  // includeAncestors = false for both modes
        );
    }, [activeFrame, isClickMode, timelineData]);

    React.useEffect(() => {
        if (popupPosition && !targetRef.current) {
            // Create invisible target div at mouse position
            const target = document.createElement('div');
            target.style.position = 'absolute';
            target.style.left = `${popupPosition.x}px`;
            target.style.top = `${popupPosition.y}px`;
            target.style.width = '1px';
            target.style.height = '1px';
            target.style.pointerEvents = 'none';
            document.body.appendChild(target);
            targetRef.current = target;
        }

        return () => {
            if (targetRef.current) {
                document.body.removeChild(targetRef.current);
                targetRef.current = null;
            }
        };
    }, [popupPosition]);

    // Conditional returns AFTER all hooks
    if (!activeFrame || !popupPosition || !flamegraphData) return null;
    if (!targetRef.current) return null;

    return (
        <Callout
            target={targetRef.current}
            onDismiss={handleDismiss}
            directionalHint={DirectionalHint.rightTopEdge}
            isBeakVisible={true}
            beakWidth={12}
            gapSpace={8}
            setInitialFocus={false}
            className="timeline-stack-popup"
            styles={{
                root: {
                    width: 1200,  // Much wider flamegraph for better readability
                    maxHeight: 600,
                    padding: 0
                }
            }}
        >
            <Stack tokens={{ childrenGap: 8 }} styles={{ root: { padding: 12 } }}>
                {/* Header */}
                <Stack.Item>
                    <Text variant="mediumPlus" block styles={{ root: { fontWeight: 600 } }}>
                        Stack View
                    </Text>
                    <Text variant="small" block styles={{ root: { color: '#605E5C' } }}>
                        Showing {isClickMode ? 'clicked' : 'hovered'} frame and all descendants
                    </Text>
                    {flamegraphData && (
                        <Text variant="small" block styles={{ root: { color: '#0078D4', fontWeight: 500, marginTop: 4 } }}>
                            Total: {flamegraphData.value.toFixed(2)} ms
                        </Text>
                    )}
                </Stack.Item>

                {/* Flamegraph */}
                <Stack.Item styles={{ root: { border: '1px solid #E1DFDD' } }}>
                    <FlameGraphCustom
                        data={flamegraphData}
                        width={1176}  // 1200 - padding
                        height={400}
                        showMethodFirst={false}  // Can pass from context if needed
                        theme="minimal"  // Minimal theme for better readability (no background colors)
                        rootAtTop={true}  // Render root at top (parents at top)
                        metric={0}  // 0 = milliseconds
                        metricContext="timeline"  // Timeline context for millisecond formatting
                        fontSize={14}  // Larger font for better readability in popup
                    />
                </Stack.Item>

                {/* Footer hint */}
                <Stack.Item>
                    <Text variant="xSmall" block styles={{ root: { color: '#605E5C', fontStyle: 'italic' } }}>
                        Click anywhere to dismiss
                    </Text>
                </Stack.Item>
            </Stack>
        </Callout>
    );
}
